import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Link,
  createSearchParams,
  generatePath,
  useLoaderData,
  useSearchParams
} from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from '@material-tailwind/react'

import { attachmentApi } from '@/store/services/attachment.service'
import { Pill } from '@/shared/components/pill'
import { AttachmentTableWithTabs } from '@/shared/components/attachments'
import { store } from '@/store'
import { useAuthorization } from '@/hooks'
import { HasAccess } from '@permify/react-role'

import { ZipDownloadRangeDialog } from '@/shared/components/zip-download-range-dialog'
import { useCreateZipAttachmentMutation } from '@/store/services/zip-attachment.service'

export const CompanyAttachments = () => {
  const { company: currentCompany } = useLoaderData()
  const [searchParams] = useSearchParams()
  const [isNavigatingToUpload, setIsNavigatingToUpload] = useState(false)
  const [showUploadLoadingMessage, setShowUploadLoadingMessage] = useState(false)
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })

  const { t } = useTranslation()
  const modalSearchParams = useMemo(() => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('attachment_tab')
    nextParams.delete('attachment_reload')
    return nextParams
  }, [searchParams])
  const currentUrlParams = modalSearchParams.toString()
    ? `?${modalSearchParams.toString()}`
    : ''

  const defaultSearchParams = useMemo(() => {
    const reloadToken = searchParams.get('attachment_reload')

    return {
      attachable_type: 'Contractor::Company',
      attachable_id: currentCompany.id,
      ...(reloadToken ? { attachment_reload: reloadToken } : {})
    }
  }, [currentCompany.id, searchParams])
  const pillOptions = useMemo(() => {
    return [
      { label: t('company.name'), value: currentCompany.name },
      { label: t('documentNumber.one'), value: currentCompany.documentNumber }
    ]
  }, [currentCompany])

  const getMore = async (searchParams) => {
    try {
      const {
        attachments = [], meta = {}
      } = await store
        .dispatch(
          attachmentApi.endpoints.getAttachments.initiate(
            `?${searchParams.toString()}`,
            { forceRefetch: true }
          )
        )
        .unwrap()

      const currentPage = Number(searchParams.get('page'))
      const hasMore = currentPage < meta?.pagination?.total_pages

      return {
        attachments,
        hasMore
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    hasAccess('company#attachments')
  }, [])

  useEffect(() => {
    if (!isNavigatingToUpload) {
      setShowUploadLoadingMessage(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setShowUploadLoadingMessage(true)
    }, 500)

    return () => window.clearTimeout(timeoutId)
  }, [isNavigatingToUpload])

  // ---- ZIP: state and logic ----
  const [isZipDialogOpen, setIsZipDialogOpen] = useState(false)
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false)
  const [existingExportInfo, setExistingExportInfo] = useState(null)
  const [pendingZipRequest, setPendingZipRequest] = useState(null)

  const [createZipAttachment, { isLoading: isCreatingZip }] =
    useCreateZipAttachmentMutation()

  const requestZip = async ({ fromDate, toDate }, { force = false } = {}) => {
    try {
      const response = await createZipAttachment({
        attachable_type: 'Contractor::Company',
        attachable_id: currentCompany.id,
        from_date: fromDate,
        to_date: toDate,
        ...(force ? { force: true } : {})
      }).unwrap()

      if (response?.conflict && response?.existing_export && !force) {
        setExistingExportInfo(response.existing_export)
        setPendingZipRequest({ fromDate, toDate })
        setIsConflictDialogOpen(true)
        return
      }

      // Success: close all related dialogs
      setIsZipDialogOpen(false)
      setIsConflictDialogOpen(false)
      setExistingExportInfo(null)
      setPendingZipRequest(null)
    } catch (error) {
      // console.error('Error requesting company ZIP', error)

      // Keep other errors with the existing behavior
    }
  }

  const handleZipConfirm = ({ fromDate, toDate }) => {
    requestZip({ fromDate, toDate }, { force: false })
  }

  const handleConflictCancel = () => {
    setIsConflictDialogOpen(false)
    setExistingExportInfo(null)
    setPendingZipRequest(null)
  }

  const handleConflictConfirm = () => {
    if (!pendingZipRequest) return
    requestZip(pendingZipRequest, { force: true })
  }

  const blockWhileNavigating = (event) => {
    if (!isNavigatingToUpload) return

    event.preventDefault()
  }

  return (
    <Dialog size='xl' className='h-4/5' open={true}>
      <DialogHeader>Detalle de documentos subidos</DialogHeader>

      <DialogBody className='relative overflow-hidden' style={{ height: 'inherit' }}>
        {isNavigatingToUpload && (
          <div className='absolute inset-0 z-10 bg-white/55 pointer-events-auto'>
            {showUploadLoadingMessage && (
              <div className='absolute inset-0 flex items-center justify-center'>
                <div className='rounded-lg bg-white px-4 py-3 shadow-md'>Cargando...</div>
              </div>
            )}
          </div>
        )}
        <div className='flex gap-3 mb-5'>
          {!!pillOptions.length &&
            pillOptions.map(({ value, label }) => (
              <Pill key={value} label={label} value={value} />
            ))}
        </div>

        <AttachmentTableWithTabs
          initialSearchParams={defaultSearchParams}
          getMore={getMore}
          initialTab={searchParams.get('attachment_tab')}
          uploadPermission='company#upload_attachments'
          onUploadNavigateStart={() => setIsNavigatingToUpload(true)}
          buildUploadPath={(attachment, currentTab) =>
            generatePath(
              '/companies/:id/attachments/:attachmentId/upload_attachment',
              {
                id: currentCompany.id,
                attachmentId: attachment.id
              }
            ) + `?${createSearchParams({
              ...Object.fromEntries(searchParams.entries()),
              attachment_tab: currentTab
            }).toString()}`
          }
        />
      </DialogBody>

      <DialogFooter className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        {/* Section: button to open the date-range dialog */}
        <div className='flex flex-col items-start gap-1'>
          <HasAccess permissions="attachment#download_batch">
            <Button
              variant='outlined'
              onClick={() => setIsZipDialogOpen(true)}
              disabled={isCreatingZip || isNavigatingToUpload}
            >
              Descargar documentos por período
            </Button>
          </HasAccess>
        </div>

        {/* Existing buttons */}
        <div className='flex gap-3'>
          <Link to={`/companies${currentUrlParams}`} onClick={blockWhileNavigating}>
            <Button
              variant='outlined'
              color='white'
              disabled={isNavigatingToUpload}
            >
              Volver
            </Button>
          </Link>

        </div>

        {/* Reusable dialog for date range + conflict */}
        <ZipDownloadRangeDialog
          open={isZipDialogOpen}
          onClose={() => setIsZipDialogOpen(false)}
          onConfirm={handleZipConfirm}
          isLoading={isCreatingZip}
          title='Descargar documentos del contratista'
          conflict={{
            open: isConflictDialogOpen,
            existingExport: existingExportInfo,
            isLoading: isCreatingZip,
            onCancel: handleConflictCancel,
            onConfirm: handleConflictConfirm
          }}
        />
      </DialogFooter>
    </Dialog>
  )
}
